import * as Y from "yjs"
import type { NormPoint, Stroke } from "@/lib/live/strokes"
import { NOTES_FIELD, insertParagraphs } from "./notesDoc"

// How long a practice session lasts, for user-facing copy. Mirrors the server's
// DailyService::PRACTICE_ROOM_TTL (app/services/daily_service.rb) — keep in sync.
export const PRACTICE_ROOM_MINUTES = 20

// Sample content for the practice classroom (#945). Both seeders write only
// into an EMPTY surface: a re-render (StrictMode double-effect) or a language
// switch never duplicates the seed, and anything the user already wrote wins.

function circlePoints(cx: number, cy: number, r: number, steps: number): NormPoint[] {
  const points: NormPoint[] = []
  for (let i = 0; i <= steps; i++) {
    const angle = (i / steps) * Math.PI * 2
    points.push([cx + r * Math.cos(angle), cy + r * Math.sin(angle)])
  }
  return points
}

function arcPoints(cx: number, cy: number, r: number, from: number, to: number, steps: number): NormPoint[] {
  const points: NormPoint[] = []
  for (let i = 0; i <= steps; i++) {
    const angle = from + (i / steps) * (to - from)
    points.push([cx + r * Math.cos(angle), cy + r * Math.sin(angle)])
  }
  return points
}

// A hand-drawn-looking smiley plus a wavy underline — enough ink that Clear and
// the erasers have something to work on. Coordinates are normalized [0,1] (the
// whiteboard's own convention), colors come from the whiteboard palette, and the
// "seed-" id prefix can never collide with makeStrokeId's `${clientId}-${counter}`.
export const PRACTICE_STROKES: Stroke[] = [
  { id: "seed-0", color: "#3b82f6", size: 4, tool: "pen", points: circlePoints(0.5, 0.38, 0.16, 32) },
  { id: "seed-1", color: "#3b82f6", size: 5, tool: "pen", points: [[0.44, 0.33], [0.44, 0.35]] },
  { id: "seed-2", color: "#3b82f6", size: 5, tool: "pen", points: [[0.56, 0.33], [0.56, 0.35]] },
  { id: "seed-3", color: "#ef4444", size: 4, tool: "pen", points: arcPoints(0.5, 0.38, 0.1, Math.PI * 0.15, Math.PI * 0.85, 16) },
  {
    id: "seed-4",
    color: "#22c55e",
    size: 3,
    tool: "pen",
    points: Array.from({ length: 33 }, (_, i): NormPoint => {
      const t = i / 32
      return [0.3 + t * 0.4, 0.68 + 0.015 * Math.sin(t * Math.PI * 4)]
    }),
  },
]

// Pre-draw the whiteboard. CollabCanvas re-renders on any change to the
// "wb:whiteboard" array, so seeding after mount paints immediately.
export function seedPracticeWhiteboard(doc: Y.Doc): void {
  const strokes = doc.getArray<Stroke>("wb:whiteboard")
  if (strokes.length > 0) return
  doc.transact(() => {
    strokes.push(PRACTICE_STROKES)
  })
}

// Pre-fill the shared notes with the localized sample text, one paragraph per
// line. TipTap's Collaboration extension is bound to this same "notes" fragment,
// so content seeded before OR after the editor mounts shows up either way.
export function seedPracticeNotes(doc: Y.Doc, lines: string[]): void {
  const fragment = doc.getXmlFragment(NOTES_FIELD)
  if (fragment.length > 0) return
  doc.transact(() => {
    insertParagraphs(fragment, lines)
  })
}
